import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Save, Trash2, Home, Edit3 } from 'lucide-react';
import { format } from 'date-fns';
import MarkdownEditor from './MarkdownEditor';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { mentionMarkdownComponents } from './Mentions';
import './FolioEditor.css';
import UserAvatar from './UserAvatar';

function FolioEditor() {
  const { projectId, folioId } = useParams();
  const navigate = useNavigate();
  const [folio, setFolio] = useState(null);
  const [projectMembers, setProjectMembers] = useState([]);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [autoSaveTimeout, setAutoSaveTimeout] = useState(null);
  const [isEditing, setIsEditing] = useState(false); // Default to preview mode

  useEffect(() => {
    if (folioId) {
      fetchFolio();
    }
  }, [folioId]);

  // Fetch project members so the @-dropdown has candidates
  useEffect(() => {
    if (projectId) {
      fetchProjectMembers();
    }
  }, [projectId]);

  useEffect(() => {
    if (folio) {
      const changed = title !== folio.title || content !== folio.content;
      setHasChanges(changed);

      // Auto-save after 2 seconds of no changes
      if (changed && autoSaveTimeout) {
        clearTimeout(autoSaveTimeout);
      }
      if (changed) {
        const timeout = setTimeout(() => {
          handleSave();
        }, 2000);
        setAutoSaveTimeout(timeout);
      }
    }
    return () => {
      if (autoSaveTimeout) clearTimeout(autoSaveTimeout);
    };
  }, [title, content, folio]);

  const fetchFolio = async () => {
    try {
      const response = await axios.get(`/folios/${folioId}`);
      setFolio(response.data);
      setTitle(response.data.title);
      setContent(response.data.content);
      // Default to preview mode for existing folios with content
      // Default to edit mode for empty folios
      setIsEditing(!response.data.content || response.data.content.trim() === '');
    } catch (error) {
      console.error('Failed to fetch folio:', error);
      alert('Failed to load folio');
    } finally {
      setLoading(false);
    }
  };

  const fetchProjectMembers = async () => {
    try {
      const response = await axios.get(`/projects/${projectId}`);
      // Project owner can be mentioned too
      const members = [...(response.data.members || [])];
      const owner = response.data.owner;
      if (owner && !members.some(m => m._id === owner._id)) {
        members.unshift(owner);
      }
      setProjectMembers(members);
    } catch (error) {
      console.error('Failed to fetch project members:', error);
    }
  };

  const handleSave = async () => {
    if (!hasChanges) return;

    setSaving(true);
    try {
      const response = await axios.put(`/folios/${folioId}`, {
        title,
        content
      });
      setFolio(response.data);
      setHasChanges(false);
      // Don't switch modes - let auto-save work silently
    } catch (error) {
      console.error('Failed to save folio:', error);
      alert('Failed to save folio');
    } finally {
      setSaving(false);
    }
  };

  // Manual save - saves and switches to preview mode
  const handleManualSave = async () => {
    await handleSave();
    setIsEditing(false);
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this folio? This cannot be undone.')) return;

    try {
      await axios.delete(`/folios/${folioId}`);
      navigate(`/project/${projectId}/folios`);
    } catch (error) {
      console.error('Failed to delete folio:', error);
      alert('Failed to delete folio');
    }
  };

  if (loading) {
    return (
      <div className="folio-editor-loading">
        <div className="loading-spinner"></div>
        <p>Loading folio...</p>
      </div>
    );
  }

  if (!folio) {
    return (
      <div className="folio-editor-error">
        <h2>Folio not found</h2>
        <button
          className="btn btn-secondary"
          onClick={() => navigate(`/project/${projectId}/folios`)}
        >
          <ArrowLeft size={18} />
          Back to Folios
        </button>
      </div>
    );
  }

  return (
    <div className="folio-editor-page">
      <div className="folio-editor-header">
        <div className="folio-editor-nav">
          <button
            className="btn btn-ghost"
            onClick={() => navigate(`/project/${projectId}`)}
            title="Back to Project"
          >
            <Home size={18} />
          </button>
          <span className="nav-separator">/</span>
          <button
            className="btn btn-ghost"
            onClick={() => navigate(`/project/${projectId}/folios`)}
          >
            <ArrowLeft size={18} />
            Back to Folios
          </button>
        </div>

        <div className="folio-editor-actions">
          {!isEditing ? (
            <button
              className="btn btn-primary"
              onClick={() => setIsEditing(true)}
            >
              <Edit3 size={18} />
              Edit
            </button>
          ) : (
            <>
              <button
                className="btn btn-primary"
                onClick={handleManualSave}
                disabled={saving || !hasChanges}
              >
                <Save size={18} />
                {saving ? 'Saving...' : hasChanges ? 'Save Changes' : 'Saved'}
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setIsEditing(false);
                  // Reset to saved values if there are unsaved changes
                  if (hasChanges && folio) {
                    setTitle(folio.title);
                    setContent(folio.content);
                    setHasChanges(false);
                  }
                }}
              >
                Cancel
              </button>
            </>
          )}
          <button className="btn btn-danger" onClick={handleDelete}>
            <Trash2 size={18} />
            Delete
          </button>
        </div>
      </div>

      <div className="folio-editor-main">
        <div className="folio-title-section">
          {isEditing ? (
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Untitled folio"
              className="folio-title-input"
            />
          ) : (
            <h1 className="folio-title-display">{title || 'Untitled Folio'}</h1>
          )}
        </div>

        <div className="folio-editor-body">
          {isEditing ? (
            <MarkdownEditor
              value={content}
              onChange={setContent}
              members={projectMembers}
              placeholder="Write your documentation in markdown...

# Heading 1
## Heading 2

**Bold text** and *italic text*

- Bullet points
- Another point

1. Numbered list
2. Another item

Type @ to mention a team member.

[Link text](https://example.com)

![Image](url)

```
Code block
```

> Blockquote"
            />
          ) : (
            <div className="folio-preview-content">
              {content ? (
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={mentionMarkdownComponents}>
                  {content}
                </ReactMarkdown>
              ) : (
                <div className="preview-empty">
                  <p>This folio is empty.</p>
                  <button
                    className="btn btn-secondary"
                    onClick={() => setIsEditing(true)}
                  >
                    <Edit3 size={18} />
                    Start Writing
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="folio-editor-footer">
          <div className="folio-author-info">
            {/* WR-018 §2: shared component — signs the path and falls back
                to initials instead of alt text. */}
            <UserAvatar user={folio.author} className="avatar avatar-sm" />
            <div>
              <div className="folio-author-name">
                Created by {folio.author?.name}
              </div>
              {folio.lastEditedBy && folio.lastEditedBy._id !== folio.author._id && (
                <div className="folio-edited-by">
                  Last edited by {folio.lastEditedBy.name}
                </div>
              )}
            </div>
          </div>
          <div className="folio-timestamp">
            Last updated {format(new Date(folio.updatedAt), 'MMM d, yyyy h:mm a')}
          </div>
        </div>
      </div>
    </div>
  );
}

export default FolioEditor;
